import PageHero from "./PageHero";
import FeaturedWork from "./FeaturedWork";
import { motion } from "framer-motion";
import { Eyebrow, Reveal, GoldButton, OutlineButton, ArrowLink, EASE } from "./motion";
import { useSEO } from "../../hooks/useSEO";

export default function CompanyTemplate({ company }) {
  useSEO(`${company.name} — Hapo Group`, company.tagline);

  return (
    <>
      <PageHero
        eyebrow="A Hapo Group Company"
        lines={[company.name]}
        description={company.tagline}
        image={company.image}
        logoOnDark={company.logoOnDark}
        logoOnLight={company.logoOnLight}
      >
        {company.comingSoon && (
          <span
            data-testid="coming-soon-badge"
            className="inline-flex items-center rounded-full bg-gold px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-ink"
          >
            Coming Soon
          </span>
        )}
      </PageHero>

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Overview">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Overview</Eyebrow>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="text-xl font-medium leading-relaxed tracking-tight text-ink md:text-2xl lg:text-3xl">
              {company.overview}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="What we do">
        <Eyebrow>What We Do</Eyebrow>
        <div className="mt-12">
          {company.whatWeDo.map((item, i) => (
            <Reveal
              key={item.title}
              delay={i * 0.05}
              className="grid gap-4 border-t border-ink/10 py-10 md:grid-cols-12 md:items-baseline"
            >
              <span className="text-4xl font-extrabold tracking-tighter text-gold md:col-span-2 lg:text-5xl">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="text-xl font-bold tracking-tight text-ink md:col-span-4 lg:text-2xl">{item.title}</h3>
              <p className="text-sm leading-loose text-smoke md:col-span-6 md:text-base">{item.text}</p>
            </Reveal>
          ))}
        </div>

        {company.partnerCallout && (
          <Reveal className="mt-14 border-l-2 border-gold bg-white p-8 lg:p-12">
            <Eyebrow>Partners</Eyebrow>
            <p className="mt-4 max-w-3xl text-sm leading-loose text-ink md:text-base">{company.partnerCallout}</p>
          </Reveal>
        )}

        {company.products &&
          company.products.map((p) => (
            <Reveal key={p.name} className="mt-14 border-l-2 border-gold bg-white p-8 lg:p-12">
              <Eyebrow>Products — built by {company.name}</Eyebrow>
              {/* Block is light in dark theme and dark in light theme, so variants are cross-paired */}
              <img src={p.logoOnLight} alt={`${p.name} logo`} className="only-dark-theme mt-6 h-10 w-auto" />
              <img src={p.logoOnDark} alt="" aria-hidden="true" className="only-light-theme mt-6 h-10 w-auto" />
              <p className="mt-4 max-w-3xl text-sm leading-loose text-ink md:text-base">{p.text}</p>
              {p.link && (
                <div className="mt-6">
                  <ArrowLink to={p.link.to} testId="product-explore-link">
                    {p.link.label}
                  </ArrowLink>
                </div>
              )}
            </Reveal>
          ))}
      </section>

      {company.gallery && <FeaturedWork />}

      {company.galleryImages && (
        <section className="bg-ink px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Studio gallery" data-testid="company-gallery">
          <Eyebrow>Inside the Studio</Eyebrow>
          <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-white sm:text-5xl">
            A look inside {company.name}.
          </h2>
          <div className="mt-16 grid gap-6 md:grid-cols-2">
            {company.galleryImages.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
                <figure className="group" data-testid={`gallery-item-${i}`}>
                  <div className="overflow-hidden">
                    <motion.img
                      initial={{ scale: 1.12 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, ease: EASE }}
                      src={g.image}
                      alt={g.title}
                      loading="lazy"
                      className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                        i === 0 ? "aspect-[21/9]" : "aspect-[16/9]"
                      }`}
                    />
                  </div>
                  <figcaption className="mt-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">{g.title}</span>
                    <span className="text-xs leading-relaxed text-smoke">{g.text}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {company.showreel && (
        <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Showreel" data-testid="company-showreel">
          <Eyebrow>Showreel</Eyebrow>
          <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
            See {company.name} in action.
          </h2>
          <Reveal className="mt-16">
            {company.showreel.type === "embed" ? (
              <div className="aspect-video w-full overflow-hidden">
                <iframe
                  src={company.showreel.url}
                  title={`${company.name} showreel`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                  data-testid="showreel-embed"
                />
              </div>
            ) : (
              <video
                src={company.showreel.url}
                poster={company.showreel.poster}
                controls
                preload="metadata"
                className="aspect-video w-full object-cover"
                data-testid="showreel-video"
              />
            )}
          </Reveal>
        </section>
      )}

      <section className="bg-ink px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Contact call to action">
        <Reveal className="flex flex-wrap items-center justify-between gap-8">
          <h2 className="max-w-xl text-3xl font-extrabold tracking-tighter text-white sm:text-4xl">
            Talk to {company.name}.
          </h2>
          <div className="flex flex-wrap gap-4">
            <GoldButton to="/contact" testId="company-contact-cta">
              Get in Touch
            </GoldButton>
            {company.cta && (
              <OutlineButton to={company.cta.to} testId="company-secondary-cta">
                {company.cta.label}
              </OutlineButton>
            )}
            <OutlineButton to="/companies" testId="company-back-cta">
              All Companies
            </OutlineButton>
          </div>
        </Reveal>
      </section>
    </>
  );
}
